import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { getLogger } from '@confluence/logger';

const logger = getLogger('SmartCreateFeedbackBanner');

interface LocalStorageItem {
	time?: number;
	count?: number;
}
const MAX_DISMISS_COUNT = 3;
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const TWO_WEEKS_IN_MS = 14 * ONE_DAY_IN_MS; // 2 weeks in milliseconds

// Cap the per-page "seen" list; oldest content IDs are dropped first.
const MAX_SEEN_CONTENT_IDS = 10;

export const STORAGE_KEYS: {
	default: string;
	slides: string;
	seen: string;
	slidesSeen: string;
} = {
	default: PERSISTED_KEYS_ON_SERVER.PERSISTED_SMART_CREATE_FEEDBACK_BANNER_LAST_DISMISSED,
	slides: PERSISTED_KEYS_ON_SERVER.PERSISTED_SMART_CREATE_FEEDBACK_BANNER_SLIDES_LAST_DISMISSED,
	seen: PERSISTED_KEYS_ON_SERVER.PERSISTED_SMART_CREATE_FEEDBACK_BANNER_SEEN_CONTENT_IDS,
	slidesSeen:
		PERSISTED_KEYS_ON_SERVER.PERSISTED_SMART_CREATE_FEEDBACK_BANNER_SLIDES_SEEN_CONTENT_IDS,
};

const DISMISSAL_PERIODS = [
	0, // Initial state, no dismissal period
	ONE_DAY_IN_MS, // 1st dismissal: 24 hours
	TWO_WEEKS_IN_MS, // 2nd dismissal: 2 weeks
];

const getLocalStorageItem = (storageKey: string): LocalStorageItem => {
	try {
		const itemString = localStorage.getItem(storageKey);
		if (!itemString) {
			return {};
		}
		const item = JSON.parse(itemString);
		if (
			typeof item === 'object' &&
			item !== null &&
			!Array.isArray(item) &&
			(typeof item.time === 'number' || item.time === undefined) &&
			(typeof item.count === 'number' || item.count === undefined)
		) {
			return item;
		}
	} catch (error) {
		logger.error`Error parsing local storage key(${storageKey}): ${error}`;
	}
	localStorage.setItem(storageKey, JSON.stringify({}));
	return {};
};

const updateLocalStorageItem = (storageKey: string, item: LocalStorageItem) => {
	try {
		localStorage.setItem(storageKey, JSON.stringify(item));
	} catch (error) {
		logger.error`Error setting local storage key(${storageKey}): ${error}`;
	}
};

// --- Escalating dismissal (24h, then 2 weeks, then permanent) ---

export const isBannerDismissed = (storageKey: string): boolean => {
	const { time = 0, count = 0 } = getLocalStorageItem(storageKey);
	const currentTime = Date.now();
	if (count >= MAX_DISMISS_COUNT) {
		return true; // Permanently dismissed
	}
	const requiredDuration = DISMISSAL_PERIODS[count] ?? 0;
	return currentTime - time < requiredDuration;
};

const updateBannerDismissal = (storageKey: string, count: number) => {
	const item = getLocalStorageItem(storageKey);
	const currentTime = Date.now();
	item.time = currentTime;
	item.count = count;
	updateLocalStorageItem(storageKey, item);
};

export const dismissBanner = (storageKey: string): void => {
	const item = getLocalStorageItem(storageKey);
	const newCount = (item.count ?? 0) + 1;
	if (newCount >= MAX_DISMISS_COUNT) {
		permanentlyDismissBanner(storageKey);
	} else {
		updateBannerDismissal(storageKey, newCount);
	}
};

export const permanentlyDismissBanner = (storageKey: string): void => {
	updateBannerDismissal(storageKey, MAX_DISMISS_COUNT);
};

// --- Per-page "seen" tracking ---

const readSeenContentIds = (storageKey: string): string[] => {
	try {
		const itemString = localStorage.getItem(storageKey);
		if (!itemString) {
			return [];
		}
		const item = JSON.parse(itemString);
		if (Array.isArray(item) && item.every((id) => typeof id === 'string')) {
			return item;
		}
	} catch (error) {
		logger.error`Error parsing local storage key(${storageKey}): ${error}`;
	}
	return [];
};

export const isBannerSeen = (storageKey: string, contentId: string): boolean =>
	readSeenContentIds(storageKey).includes(contentId);

export const markBannerSeen = (storageKey: string, contentId: string): void => {
	const seen = readSeenContentIds(storageKey);
	if (seen.includes(contentId)) {
		return;
	}
	// Cap the list, dropping the oldest entries first.
	const capped = [...seen, contentId].slice(-MAX_SEEN_CONTENT_IDS);
	try {
		localStorage.setItem(storageKey, JSON.stringify(capped));
	} catch (error) {
		logger.error`Error setting local storage key(${storageKey}): ${error}`;
	}
};
